import { useEffect, useMemo, useState } from 'react';
import Widget from '../../../components/Dashboard/Widget';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import useUsers from 'contexts/basicData/useUsers';
import { formatDateTime } from 'utils/date-helpers';
import CaseOpenLink from 'components/CaseOpenLink';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import { AssignmentDTO } from 'api/model';
import { LongTitles, TLWidgetsEnum, UPDATE_INTERVAL } from '../utils';
import { useTransportLogisticsGetAssignmentsWithHotelBookedYesterday } from 'api/transport-logistics/transport-logistics';
import {
  Centered,
  getTableRowClassName,
} from 'components/Dashboard/table-helpers';

const TABLE_NAME = 'hotel-booked-yesterday';

const HotelBookedYesterdayWidget = () => {
  const [assignments, setAssignments] = useState<AssignmentDTO[]>([]);
  const users = useUsers();
  const {
    dashboardState: { daterange, selectedBusinessUnits, searchText },
  } = useDashboardControls();

  const getAssignments =
    useTransportLogisticsGetAssignmentsWithHotelBookedYesterday(
      {
        // The backend returns hotel bookings for the day BEFORE this date (yesterday relative
        // to the selected viewing day), so the widget honours the dashboard date picker.
        startDate: daterange[0] ?? undefined,
        endDate: daterange[1] ?? undefined,
        businessUnitIDs: selectedBusinessUnits,
      },
      {
        query: {
          refetchInterval: UPDATE_INTERVAL,
        },
      }
    );

  useEffect(() => {
    if (getAssignments.data) {
      setAssignments(getAssignments.data);
    } else {
      setAssignments([] as AssignmentDTO[]);
    }
  }, [getAssignments.data]);

  const filteredAssignments = useMemo(() => {
    if (!searchText.length) {
      return assignments;
    }
    const needle = searchText.toLowerCase();
    return assignments.filter((assignment) => {
      const ftName = users[assignment.assignedToID ?? -1]?.name ?? '';
      const stayOverInfo = assignment.stayOverInfo ?? '';
      return (
        ftName.toLowerCase().includes(needle) ||
        stayOverInfo.toLowerCase().includes(needle) ||
        assignment.caseID.toString().includes(needle)
      );
    });
  }, [assignments, searchText, users]);

  const columnSettings: ColumnSetting<AssignmentDTO, any>[] = useMemo(() => {
    const columns: ColumnSetting<AssignmentDTO, any>[] = [
      {
        head: 'Nr',
        render: (e, t, f, i) => i + 1,
        width: 15,
      },
      {
        head: 'Fälttestare',
        render: (row) => (
          <span>
            {users[row.assignedToID ?? -1]
              ? users[row.assignedToID ?? -1].name
              : ''}
          </span>
        ),
        width: 100,
        sortFunction: (a, b) =>
          (users[a.assignedToID ?? -1]?.name ?? '').localeCompare(
            users[b.assignedToID ?? -1]?.name ?? ''
          ),
      },
      {
        head: 'Datum',
        render: (row) => `${formatDateTime(row.bookedTo)}`,
        width: 100,
        sortFunction: (a, b) => a.bookedTo.getTime() - b.bookedTo.getTime(),
      },
      {
        head: 'Hotellinfo',
        render: (row) => (
          <span title={row.stayOverInfo ?? ''}>{row.stayOverInfo ?? '-'}</span>
        ),
        width: 200,
        sortFunction: (a, b) =>
          (a.stayOverInfo ?? '').localeCompare(b.stayOverInfo ?? ''),
      },
      {
        head: ' ',
        render: (row) => (
          <Centered>
            <CaseOpenLink caseID={row.caseID} />
          </Centered>
        ),
        width: 20,
      },
    ];
    return columns;
  }, [users]);

  return (
    <Widget
      title={LongTitles[TLWidgetsEnum.HotelBookedYesterday]}
      count={assignments.length}
      isLoading={getAssignments.isLoading}
      info="Uppdrag med hotell bokat (StayOverType = HOTEL) och bokat till-tid i gårdagens svenska dygn."
    >
      <Table
        columnSettings={columnSettings}
        rows={filteredAssignments}
        rowClassName={(row) => getTableRowClassName(TABLE_NAME, row.id)}
        useColumnWidthAsFlexAmount
      />
    </Widget>
  );
};

export default HotelBookedYesterdayWidget;
